import React, {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaBook,
  FaBookOpen,
  FaCheck,
  FaEdit,
  FaHome,
  FaLock,
  FaMinus,
  FaPlus,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const MdlAddClassSubjectList = React.lazy(() =>
  import("../dialog/MdlAddClassSubjectList")
);
const MdlEditClassSubject = React.lazy(() =>
  import("../dialog/MdlEditClassSubject")
);
const MdlDeleteClassSubject = React.lazy(() =>
  import("../dialog/MdlDeleteClassSubject")
);

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif';

const TYPE_TONE = {
  compulsory: "cs-tone--compulsory",
  optional: "cs-tone--optional",
  elective: "cs-tone--elective",
  practical: "cs-tone--practical",
  theory: "cs-tone--theory",
};

//Created By Cursor
function subjectTypeTone(type) {
  const key = String(type || "")
    .trim()
    .toLowerCase();
  return TYPE_TONE[key] || "cs-tone--default";
}

//Created By Cursor
function isDeleteLocked(item) {
  return (
    String(item?.delete_locked) === "1" || item?.delete_locked === true
  );
}

//Created By Cursor
function deleteLockReason(item) {
  const reason = String(item?.delete_lock_reason || "").trim();
  if (reason) return reason;
  const exams = Array.isArray(item?.delete_lock_exams)
    ? item.delete_lock_exams.filter(Boolean)
    : [];
  if (exams.length === 1) {
    return `Used in exam "${exams[0]}" for this class.`;
  }
  if (exams.length > 1) {
    return `Used in ${exams.length} exams for this class.`;
  }
  return "Used in an exam for this class.";
}

//Created By Cursor
function subjectKey(item) {
  return String(item?.subject_id || item?.id || "").trim();
}

//Created By Cursor
const SubjectCard = memo(function SubjectCard({
  item,
  index,
  busy,
  selected,
  onToggleSelect,
  onEdit,
  onDelete,
}) {
  const typeLabel = item.subject_type || "—";
  const deleteLocked = isDeleteLocked(item);
  const lockReason = deleteLocked ? deleteLockReason(item) : "";
  const canSelect = !busy && !deleteLocked;

  //Created By Cursor
  const handleCardClick = (e) => {
    if (!canSelect) return;
    if (e.target.closest(".cs-card__actions, .cs-card__check")) return;
    onToggleSelect(item);
  };

  return (
    <article
      className={`cs-card${deleteLocked ? " is-delete-locked" : ""}${
        selected ? " is-selected" : ""
      }${canSelect ? " is-selectable" : ""}`}
      style={{ "--rn-i": index }}
      onClick={handleCardClick}
      role={canSelect ? "button" : undefined}
      tabIndex={canSelect ? 0 : undefined}
      onKeyDown={
        canSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleSelect(item);
              }
            }
          : undefined
      }
      aria-pressed={canSelect ? !!selected : undefined}
    >
      <label
        className={`cs-card__check${selected ? " is-on" : ""}${
          deleteLocked ? " is-locked" : ""
        }`}
        title={
          deleteLocked
            ? lockReason
            : selected
            ? "Deselect subject"
            : "Select subject"
        }
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={!!selected}
          disabled={!canSelect}
          onChange={() => onToggleSelect(item)}
          aria-label={`Select ${item.subject_name || "subject"}`}
        />
        <span className="cs-card__check-box" aria-hidden>
          {selected ? <FaCheck /> : deleteLocked ? <FaLock /> : null}
        </span>
      </label>
      <div className="cs-card__icon" aria-hidden>
        <FaBook />
      </div>
      <div className="cs-card__body min-w-0">
        <h3 className="cs-card__name">{item.subject_name || "—"}</h3>
        <div className="cs-card__meta">
          <span className={`cs-chip ${subjectTypeTone(item.subject_type)}`}>
            {typeLabel}
          </span>
          {deleteLocked ? (
            <span className="cs-chip cs-tone--locked" title={lockReason}>
              <FaLock aria-hidden /> Delete locked
            </span>
          ) : null}
        </div>
        {deleteLocked ? (
          <p className="cs-card__lock-reason" title={lockReason}>
            <FaLock aria-hidden /> {lockReason}
          </p>
        ) : null}
      </div>
      <div
        className="cs-card__actions"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="cs-icon-btn cs-icon-btn--edit"
          title="Edit subject type"
          aria-label={`Edit ${item.subject_name || "subject"}`}
          disabled={busy}
          onClick={() => onEdit(item)}
        >
          <FaEdit aria-hidden />
        </button>
        <button
          type="button"
          className={`cs-icon-btn cs-icon-btn--danger${
            deleteLocked ? " is-locked" : ""
          }`}
          title={deleteLocked ? lockReason : "Remove subject"}
          aria-label={
            deleteLocked
              ? `Delete locked: ${lockReason}`
              : `Delete ${item.subject_name || "subject"}`
          }
          disabled={busy || deleteLocked}
          onClick={() => onDelete(item)}
        >
          {deleteLocked ? <FaLock aria-hidden /> : <FaTrash aria-hidden />}
        </button>
      </div>
    </article>
  );
});

//Created By Cursor
function ClassSubject() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [className, setClassName] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [classPosition, setClassPosition] = useState("0");
  const [subjects, setSubjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [bulkDeleteSubjects, setBulkDeleteSubjects] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //Created By Cursor
  const fetchClassSubjects = useCallback(
    async (class_id, section_id, session_id, { soft = false } = {}) => {
      if (!class_id || !session_id) {
        setSubjects([]);
        setLoading(false);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        return;
      }

      const reqId = ++reqIdRef.current;
      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getClassSubject.php`, {
          params: {
            school_code: schoolCode,
            session_id,
            class_id,
            section_id,
          },
        });
        if (reqId !== reqIdRef.current) return;

        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          setSubjects(res.data.data);
          setSelectedIds(new Set());
        } else {
          setSubjects([]);
          setSelectedIds(new Set());
          if (soft || res.data?.api_remark) {
            myToast.notify_warning(
              res.data?.api_remark || "No class subjects found."
            );
          }
        }
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        console.error("[ClassSubject] load error:", error);
        setSubjects([]);
        setSelectedIds(new Set());
        myToast.notify_warning("Failed to load class subjects.");
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    []
  );

  //Created By Cursor
  const handleClassSelect = useCallback(
    (class_id, section_id, session_id, selected_position, class_name) => {
      setClassId(class_id || "");
      setClassName(class_name || "");
      setSectionId(section_id || "");
      setSessionId(session_id || "");
      setClassPosition(
        selected_position != null ? String(selected_position) : "0"
      );
      setSelectedIds(new Set());
      fetchClassSubjects(class_id, section_id, session_id);
    },
    [fetchClassSubjects]
  );

  //Created By Cursor
  const refreshSoft = useCallback(() => {
    fetchClassSubjects(classId, sectionId, sessionId, { soft: true });
  }, [fetchClassSubjects, classId, sectionId, sessionId]);

  const typeSummary = useMemo(() => {
    const map = {};
    subjects.forEach((s) => {
      const key = String(s.subject_type || "Other").trim() || "Other";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [subjects]);

  //Created By Cursor
  const deletableSubjects = useMemo(
    () => subjects.filter((s) => !isDeleteLocked(s) && subjectKey(s)),
    [subjects]
  );

  //Created By Cursor
  const selectedSubjects = useMemo(
    () => subjects.filter((s) => selectedIds.has(subjectKey(s))),
    [subjects, selectedIds]
  );

  const allDeletableSelected =
    deletableSubjects.length > 0 &&
    deletableSubjects.every((s) => selectedIds.has(subjectKey(s)));
  const someDeletableSelected =
    deletableSubjects.some((s) => selectedIds.has(subjectKey(s))) &&
    !allDeletableSelected;

  //Created By Cursor
  const toggleSelect = useCallback((item) => {
    if (isDeleteLocked(item)) {
      myToast.notify_warning(deleteLockReason(item));
      return;
    }
    const key = subjectKey(item);
    if (!key) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  //Created By Cursor
  const toggleSelectAllDeletable = useCallback(() => {
    if (!deletableSubjects.length) {
      myToast.notify_warning("No deletable subjects to select.");
      return;
    }
    setSelectedIds((prev) => {
      const allSelected = deletableSubjects.every((s) =>
        prev.has(subjectKey(s))
      );
      if (allSelected) return new Set();
      return new Set(deletableSubjects.map((s) => subjectKey(s)));
    });
  }, [deletableSubjects]);

  //Created By Cursor
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  //Created By Cursor
  const openAdd = useCallback(() => {
    if (!classId || !sessionId) {
      myToast.notify_warning("Select a class first.");
      return;
    }
    setAddOpen(true);
  }, [classId, sessionId]);

  //Created By Cursor
  const closeAdd = useCallback(() => {
    setAddOpen(false);
    fetchClassSubjects(classId, sectionId, sessionId, { soft: true });
  }, [fetchClassSubjects, classId, sectionId, sessionId]);

  //Created By Cursor
  const openEdit = useCallback((subject) => {
    setSelectedSubject(subject);
    setEditOpen(true);
  }, []);

  //Created By Cursor
  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setSelectedSubject(null);
    fetchClassSubjects(classId, sectionId, sessionId, { soft: true });
  }, [fetchClassSubjects, classId, sectionId, sessionId]);

  //Created By Cursor
  const openDelete = useCallback((subject) => {
    if (isDeleteLocked(subject)) {
      myToast.notify_warning(deleteLockReason(subject));
      return;
    }
    setBulkDeleteSubjects(null);
    setSelectedSubject(subject);
    setDeleteOpen(true);
  }, []);

  //Created By Cursor
  const openBulkDelete = useCallback(() => {
    const rows = subjects.filter((s) => selectedIds.has(subjectKey(s)));
    const unlocked = rows.filter((s) => !isDeleteLocked(s));
    if (!unlocked.length) {
      myToast.notify_warning(
        rows.length
          ? "Selected subjects are locked and cannot be deleted."
          : "Select at least one subject to delete."
      );
      return;
    }
    setSelectedSubject(null);
    setBulkDeleteSubjects(unlocked);
    setDeleteOpen(true);
  }, [subjects, selectedIds]);

  //Created By Cursor
  const closeDelete = useCallback(() => {
    setDeleteOpen(false);
    setSelectedSubject(null);
    setBulkDeleteSubjects(null);
    setSelectedIds(new Set());
    fetchClassSubjects(classId, sectionId, sessionId, { soft: true });
  }, [fetchClassSubjects, classId, sectionId, sessionId]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const busy = loading || refreshing;
  const classLabel = className
    ? `${className}${sectionId ? ` · Sec ${sectionId}` : ""}`
    : "Select a class to manage subjects";

  return (
    <div
      className="content payroll-page sm-page cs-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="cs-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Class Subject</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Class Subjects</h1>
              <p className="sm-hero__sub">{classLabel}</p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              onClick={refreshSoft}
              disabled={busy || !classId}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="cs-hero-btn"
              onClick={openAdd}
              disabled={!classId || busy}
            >
              <FaPlus aria-hidden /> Add Subject
            </button>
          </div>
        </header>

        <div className="cs-content">
          <section className="cs-filter" aria-label="Class filter">
            <SelectClass
              action={handleClassSelect}
              selected_position={classPosition || "0"}
            />
          </section>

          <div className="cs-kpi-row" aria-label="Subject summary">
            <span className="cs-kpi">
              <FaBook aria-hidden />
              {subjects.length} subject{subjects.length === 1 ? "" : "s"}
            </span>
            {typeSummary.slice(0, 4).map(([type, count]) => (
              <span key={type} className="cs-kpi cs-kpi--soft">
                {type}: {count}
              </span>
            ))}
          </div>

          {subjects.length > 0 ? (
            <div
              className={`cs-bulk-bar${
                selectedIds.size > 0 ? " has-selection" : ""
              }`}
              aria-label="Bulk selection"
            >
              <button
                type="button"
                className={`cs-select-all${
                  allDeletableSelected
                    ? " is-on"
                    : someDeletableSelected
                    ? " is-partial"
                    : ""
                }`}
                onClick={toggleSelectAllDeletable}
                disabled={busy || !deletableSubjects.length}
                title={
                  allDeletableSelected
                    ? "Clear selection"
                    : "Select all deletable subjects"
                }
                aria-pressed={allDeletableSelected}
              >
                <span className="cs-select-all__box" aria-hidden>
                  {allDeletableSelected ? (
                    <FaCheck />
                  ) : someDeletableSelected ? (
                    <FaMinus />
                  ) : null}
                </span>
                <span className="cs-select-all__label">
                  {allDeletableSelected ? "Deselect all" : "Select all"}
                </span>
              </button>
              <div className="cs-bulk-meta">
                <span
                  className={`cs-bulk-pill${
                    selectedIds.size > 0 ? " is-active" : ""
                  }`}
                >
                  {selectedIds.size} selected
                </span>
                <span className="cs-bulk-hint">
                  {deletableSubjects.length} deletable
                  {subjects.length - deletableSubjects.length > 0
                    ? ` · ${subjects.length - deletableSubjects.length} locked`
                    : ""}
                  {selectedIds.size === 0
                    ? " · tap a card to select"
                    : ""}
                </span>
              </div>
              <div className="cs-bulk-actions">
                {selectedIds.size > 0 ? (
                  <button
                    type="button"
                    className="cs-bulk-btn cs-bulk-btn--ghost"
                    onClick={clearSelection}
                    disabled={busy}
                  >
                    Clear
                  </button>
                ) : null}
                <button
                  type="button"
                  className="cs-bulk-btn cs-bulk-btn--danger"
                  onClick={openBulkDelete}
                  disabled={busy || selectedSubjects.length === 0}
                >
                  <FaTrash aria-hidden /> Delete
                  {selectedSubjects.length
                    ? ` (${selectedSubjects.length})`
                    : ""}
                </button>
              </div>
            </div>
          ) : null}

          <section className="cs-section-card" aria-label="Assigned subjects">
            <div className="cs-section-card__body">
              {loading && !subjects.length ? (
                <div className="cs-loading">
                  <MyLoader />
                </div>
              ) : !classId ? (
                <div className="cs-empty">
                  <p className="cs-empty__title">Choose a class</p>
                  <p className="cs-empty__hint">
                    Pick session, class and section above to view subjects.
                  </p>
                </div>
              ) : subjects.length === 0 ? (
                <div className="cs-empty">
                  <p className="cs-empty__title">No subjects assigned</p>
                  <p className="cs-empty__hint">
                    Use Add Subject to assign subjects to this class.
                  </p>
                  <button
                    type="button"
                    className="cs-empty__btn"
                    onClick={openAdd}
                  >
                    <FaPlus aria-hidden /> Add Subject
                  </button>
                </div>
              ) : (
                <div className="cs-grid">
                  {subjects.map((item, index) => {
                    const key = subjectKey(item);
                    return (
                      <SubjectCard
                        key={key || `${item.subject_name}-${index}`}
                        item={item}
                        index={index}
                        busy={busy}
                        selected={key ? selectedIds.has(key) : false}
                        onToggleSelect={toggleSelect}
                        onEdit={openEdit}
                        onDelete={openDelete}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <Suspense fallback={null}>
        {addOpen ? (
          <MdlAddClassSubjectList
            actionClose={closeAdd}
            class_id={classId}
            class_name={className}
            class_subject={subjects}
            section_id={sectionId}
            session_id={sessionId}
          />
        ) : null}
        {editOpen && selectedSubject ? (
          <MdlEditClassSubject
            actionClose={closeEdit}
            subject={selectedSubject}
            session_id={sessionId}
            section_id={sectionId}
            class_id={classId}
          />
        ) : null}
        {deleteOpen && (selectedSubject || bulkDeleteSubjects?.length) ? (
          <MdlDeleteClassSubject
            actionClose={closeDelete}
            subject={selectedSubject}
            subjects={bulkDeleteSubjects || undefined}
            session_id={sessionId}
            section_id={sectionId}
            class_id={classId}
          />
        ) : null}
      </Suspense>

      {busy && subjects.length > 0 ? <MyLoader /> : null}
    </div>
  );
}

export default ClassSubject;
